import { useQuery } from "@apollo/client";
import ExecutivesOrDirectorSheetContent from "components/company/v2/ExecutivesOrDirectorSheetContent";
import type { TExecutiveOrDirector } from "components/company/v2/types";
import useAmericanIssuer from "components/company/v3/context/hooks/useAmericanIssuer";
import Avatar from "components/core/Avatar";
import SadFaceIcon from "components/icons/SadFaceIcon";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import { Button } from "components/ui/Button";
import { Sheet } from "components/ui/Sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/Tabs";
import formatMoneyValue from "components/utils/formatMoneyValue";
import getYear from "date-fns/getYear";
import parseISO from "date-fns/parseISO";
import type { IExecutiveCompensationHistoryItemFieldsFragment } from "lib/graphql/fragments/__generated__/ExecutiveCompensationHistoryItemFields.generated";
import ExecutivesAndDirectorsQuery from "lib/graphql/queries/ExecutivesAndDirectorsQuery";
import type {
  IExecutivesAndDirectorsQuery,
  IExecutivesAndDirectorsQueryVariables,
} from "lib/graphql/queries/__generated__/ExecutivesAndDirectorsQuery.generated";
import usePagination from "lib/hooks/usePagination";
import { ArrowUpIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { ISecurityCountry } from "types/graphqlTypes";

interface AmericanGovernanceContentProps {
  tickerSymbol: string;
  isRenderPageDefault: boolean;
}

export default function AmericanGovernanceContent({
  tickerSymbol,
  isRenderPageDefault,
}: AmericanGovernanceContentProps) {
  const { data, loading } = useQuery<IExecutivesAndDirectorsQuery, IExecutivesAndDirectorsQueryVariables>(
    ExecutivesAndDirectorsQuery,
    {
      variables: {
        tickerSymbol,
        country: ISecurityCountry.Us,
      },
    },
  );

  if (loading) {
    return <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />;
  }

  if (
    !data ||
    data.issuerByTickerSymbol?.__typename !== "AmericanCompanyIssuer" ||
    !data.issuerByTickerSymbol.executivesAndDirectors
  ) {
    return (
      <div className="flex h-full w-full items-center justify-center p-4 font-aeonik">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
          <div>
            <SadFaceIcon />
          </div>

          <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
        </div>
      </div>
    );
  }

  const { executivesAndDirectors } = data.issuerByTickerSymbol;

  return (
    <Tabs className="flex h-auto w-full flex-col justify-start font-aeonik font-normal" defaultValue="OFFICERS">
      <TabsList className="flex w-fit">
        <TabsTrigger value="OFFICERS">Officers</TabsTrigger>
        <TabsTrigger value="DIRECTORS">Directors</TabsTrigger>
      </TabsList>

      <TabsContent className="-mx-2 h-full border-none p-0" value="OFFICERS">
        <ExecutivesAndDirectorsList
          executivesAndDirectors={executivesAndDirectors.filter((person) => person.isOfficer)}
          isRenderPageDefault={isRenderPageDefault}
        />
      </TabsContent>

      <TabsContent className="-mx-2 h-full border-none p-0" value="DIRECTORS">
        <ExecutivesAndDirectorsList
          executivesAndDirectors={executivesAndDirectors.filter((person) => person.isDirector && !person.isOfficer)}
          isRenderPageDefault={isRenderPageDefault}
        />
      </TabsContent>
    </Tabs>
  );
}

interface IExecutivesAndDirectorsListProps {
  executivesAndDirectors: TExecutiveOrDirector[];
  isRenderPageDefault: boolean;
}
function ExecutivesAndDirectorsList({ executivesAndDirectors, isRenderPageDefault }: IExecutivesAndDirectorsListProps) {
  const [activeExecutiveOrDirector, setActiveExecutiveOrDirector] = useState<TExecutiveOrDirector | null>(null);
  const issuer = useAmericanIssuer();

  const pagination = usePagination({
    items: executivesAndDirectors,
    itemsPerPage: isRenderPageDefault ? 3 : 6,
    mode: "slice",
  });

  return (
    <div className="flex h-full w-full flex-col items-end">
      <ul className="h-full min-h-[325px] w-full divide-y divide-gray-100">
        {pagination.currentItems.map((person) => {
          const executiveCompensationHistoryInUSD = [...person.executiveCompensationHistoryInUSD].sort((a, b) => {
            const aDate = parseISO(a.reportDate);
            const bDate = parseISO(b.reportDate);
            return aDate > bDate ? -1 : 1;
          });
          const latestExecutiveCompensation =
            executiveCompensationHistoryInUSD.length > 0 ? executiveCompensationHistoryInUSD[0] : null;
          const previousExecutiveCompensation =
            executiveCompensationHistoryInUSD.length > 1 ? executiveCompensationHistoryInUSD[1] : null;

          return (
            <li key={person.id} className="flex items-center justify-between gap-x-6 px-4 py-5">
              <div className="flex gap-x-4">
                <Avatar
                  src={null}
                  hashValue={person.id}
                  alt={`${person.firstName} ${person.lastName}`}
                  size={40}
                  squircle={true}
                />

                <div className="min-w-0 flex-auto space-y-1">
                  <p className="text-sm font-semibold leading-6 text-gray-900">{person.fullName}</p>
                  <p className="w-48 truncate text-xs font-medium tracking-wider text-slate-500">
                    {person.standardTitle}
                  </p>
                  {person.age && (
                    <small className="text-xs font-medium italic text-slate-400">{person.age} years old</small>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-x-4">
                <div className="hidden sm:flex sm:flex-col sm:items-end">
                  {latestExecutiveCompensation && (
                    <ExecutiveCompensationHighlight
                      executiveCompensation={latestExecutiveCompensation}
                      previousExecutiveCompensation={previousExecutiveCompensation}
                    />
                  )}
                </div>
                {person.executiveCompensationHistoryInUSD.length > 0 && (
                  <button type="button" onClick={() => setActiveExecutiveOrDirector(person)}>
                    <ChevronRightIcon
                      className="h-6 w-6 flex-none text-gray-400 hover:text-gray-600"
                      aria-hidden="true"
                    />
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {(pagination.hasNextPage || pagination.hasPreviousPage) && (
        <div className="inline-flex rounded-md border border-slate-300 bg-white shadow-sm">
          <Button
            variant="ghost"
            className="px-2"
            disabled={!pagination.hasPreviousPage}
            onClick={() => pagination.goToPreviousPage()}
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </Button>
          <div className="flex items-center justify-center border-x border-slate-300 px-2 text-sm text-slate-400">
            {pagination.currentPage}/{pagination.pages[pagination.pages.length - 1]}
          </div>
          <Button
            variant="ghost"
            className="px-2"
            disabled={!pagination.hasNextPage}
            onClick={() => pagination.goToNextPage()}
          >
            <ChevronLeftIcon className="h-5 w-5 rotate-180 transform" />
          </Button>
        </div>
      )}

      <Sheet onOpenChange={() => setActiveExecutiveOrDirector(null)} open={activeExecutiveOrDirector !== null}>
        {activeExecutiveOrDirector && (
          <ExecutivesOrDirectorSheetContent issuer={issuer} person={activeExecutiveOrDirector} />
        )}
      </Sheet>
    </div>
  );
}

interface IExecutiveCompensationHighlightProps {
  executiveCompensation: IExecutiveCompensationHistoryItemFieldsFragment;
  previousExecutiveCompensation?: IExecutiveCompensationHistoryItemFieldsFragment | null;
}

function ExecutiveCompensationHighlight({
  executiveCompensation,
  previousExecutiveCompensation,
}: IExecutiveCompensationHighlightProps) {
  const totalCompensation = executiveCompensation.totalCompensation || executiveCompensation.calculatedTotal;
  const dollarValue = formatMoneyValue(totalCompensation, "short", "en-US");

  const hadIncrease = previousExecutiveCompensation
    ? totalCompensation >
      (previousExecutiveCompensation.totalCompensation || previousExecutiveCompensation.calculatedTotal)
    : false;
  const increaseInPercent = previousExecutiveCompensation
    ? (totalCompensation /
        (previousExecutiveCompensation.totalCompensation || previousExecutiveCompensation.calculatedTotal)) *
        100 -
      100
    : 0;
  const formattedIncreaseInPercent = `${Math.abs(increaseInPercent).toFixed(1)}%`;
  return (
    <>
      <div className="flex items-center space-x-2">
        <strong className="text-sm font-bold text-emerald-700">${dollarValue}</strong>
        {hadIncrease && (
          <span className="flex items-center rounded-md bg-emerald-50 px-1.5 py-1 text-xs font-bold text-emerald-700">
            <ArrowUpIcon className="h-4 w-4" />
            {formattedIncreaseInPercent}
          </span>
        )}
      </div>

      <small className="mt-1 text-xs italic tracking-wide text-gray-600">
        Total Compensation, {getYear(parseISO(executiveCompensation.reportDate))}
      </small>
    </>
  );
}
